<script>
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';

export default {
  components: {
    NextButton,
    SpinnerLoader,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      session: {},
      isLoading: false,
      action: '',
      poller: null,
    };
  },
  computed: {
    status() {
      return this.session.status || 'UNKNOWN';
    },
    isConnected() {
      return this.status.toUpperCase() === 'CONNECTED';
    },
    showQr() {
      return this.session.qrcode && !this.isConnected;
    },
  },
  mounted() {
    this.fetchSession();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async fetchSession() {
      try {
        this.isLoading = true;
        const response = await InboxesAPI.getWhatsappWebSession(this.inbox.id);
        this.session = response.data;
        this.syncPolling();
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.WHATSAPP_WEB.LOAD_ERROR')
        );
      } finally {
        this.isLoading = false;
      }
    },
    async runAction(action, requestName) {
      try {
        this.action = action;
        const response = await InboxesAPI[requestName](this.inbox.id);
        this.session = response.data;
        this.syncPolling();
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.WHATSAPP_WEB.ACTION_ERROR')
        );
      } finally {
        this.action = '';
      }
    },
    startSession() {
      this.runAction('start', 'startWhatsappWebSession');
    },
    logoutSession() {
      this.runAction('logout', 'logoutWhatsappWebSession');
    },
    refreshSession() {
      this.runAction('refresh', 'refreshWhatsappWebSession');
    },
    syncPolling() {
      if (this.isConnected) {
        this.stopPolling();
        return;
      }
      if (!this.poller) {
        this.poller = window.setInterval(this.refreshSession, 5000);
      }
    },
    stopPolling() {
      if (!this.poller) return;
      window.clearInterval(this.poller);
      this.poller = null;
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex items-center justify-between gap-4">
      <div>
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_WEB.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-0">
          {{ $t('INBOX_MGMT.WHATSAPP_WEB.DESCRIPTION') }}
        </p>
      </div>
      <span
        class="px-3 py-1 rounded-md text-sm font-medium"
        :class="
          isConnected
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-amber-3 text-n-amber-11'
        "
      >
        {{ status }}
      </span>
    </div>

    <div v-if="isLoading" class="flex h-40 items-center justify-center">
      <SpinnerLoader :size="24" class="text-n-blue-9" />
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-[18rem_1fr] gap-6">
      <div
        class="flex items-center justify-center border border-n-weak rounded-lg bg-n-surface-2 min-h-[18rem]"
      >
        <img
          v-if="showQr"
          :src="session.qrcode"
          class="w-64 h-64 object-contain"
          alt="WhatsApp Web QR"
        />
        <div v-else class="text-sm text-n-slate-11 px-4 text-center">
          {{
            isConnected
              ? $t('INBOX_MGMT.WHATSAPP_WEB.CONNECTED')
              : $t('INBOX_MGMT.WHATSAPP_WEB.NO_QR')
          }}
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <label>
          {{ $t('INBOX_MGMT.WHATSAPP_WEB.SESSION') }}
          <input :value="session.session_name" type="text" readonly />
        </label>
        <label>
          {{ $t('INBOX_MGMT.WHATSAPP_WEB.PHONE_NUMBER') }}
          <input :value="session.phone_number || '-'" type="text" readonly />
        </label>
        <label>
          {{ $t('INBOX_MGMT.WHATSAPP_WEB.WEBHOOK_URL') }}
          <input :value="session.webhook_url" type="text" readonly />
        </label>

        <div class="flex flex-wrap gap-3 mt-2">
          <NextButton
            blue
            solid
            :is-loading="action === 'start'"
            :label="$t('INBOX_MGMT.WHATSAPP_WEB.START')"
            @click="startSession"
          />
          <NextButton
            slate
            faded
            :is-loading="action === 'refresh'"
            :label="$t('INBOX_MGMT.WHATSAPP_WEB.REFRESH')"
            @click="refreshSession"
          />
          <NextButton
            ruby
            faded
            :is-loading="action === 'logout'"
            :label="$t('INBOX_MGMT.WHATSAPP_WEB.LOGOUT')"
            @click="logoutSession"
          />
        </div>
      </div>
    </div>
  </div>
</template>
